import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import SectionHeading from "@/components/SectionHeading";
import { FadeUp } from "@/components/Reveal";
import { ArrowButton } from "@/components/ArrowButton";
import { waTopic } from "@/data/site";

export const TIMELINE_STEPS = [
  { n: "01", title: "Entrevista inicial", text: "Escuta da demanda, da história e do contexto para compreender o que motiva a avaliação." },
  { n: "02", title: "Planejamento da avaliação", text: "Definição individualizada dos procedimentos conforme idade, demanda e objetivos." },
  { n: "03", title: "Sessões de avaliação", text: "Encontros estruturados com instrumentos e atividades adequados a cada processo." },
  { n: "04", title: "Integração e análise das informações", text: "Cruzamento criterioso dos dados, da história e dos contextos de vida." },
  { n: "05", title: "Devolutiva e documentação pertinente", text: "Apresentação dos resultados com clareza e entrega da documentação conforme as normas profissionais." },
];

const Timeline = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 75%", "end 65%"] });
  const scaleY = useSpring(scrollYProgress, { stiffness: 60, damping: 20 });

  return (
    <section data-testid="timeline-section" className="py-20 lg:py-28">
      <div className="container-x">
        <SectionHeading
          eyebrow="O processo"
          title="Como funciona a avaliação neuropsicológica"
          testId="timeline"
        />
        <div ref={ref} className="relative mt-16 max-w-3xl">
          <div aria-hidden="true" className="absolute left-[19px] top-0 h-full w-px bg-raiz-900/10 sm:left-[23px]" />
          <motion.div
            aria-hidden="true"
            style={{ scaleY }}
            className="absolute left-[19px] top-0 h-full w-px origin-top bg-raiz-600 sm:left-[23px]"
          />
          <ol className="space-y-12">
            {TIMELINE_STEPS.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.05} y={20}>
                <li data-testid={`timeline-step-${s.n}`} className="relative flex gap-6 pl-0 sm:gap-8">
                  <span className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-raiz-600/40 bg-paper font-serif text-sm text-raiz-800 sm:h-12 sm:w-12">
                    {s.n}
                  </span>
                  <div className="pb-2 pt-1">
                    <h3 className="font-serif text-xl font-normal text-ink sm:text-2xl">{s.title}</h3>
                    <p className="mt-2 max-w-lg text-sm leading-relaxed text-ink/65">{s.text}</p>
                  </div>
                </li>
              </FadeUp>
            ))}
          </ol>
        </div>
        <FadeUp delay={0.1} className="mt-14 max-w-2xl">
          <p className="text-sm leading-relaxed text-ink/60">
            Cada processo é individualizado. A quantidade de encontros e os procedimentos utilizados podem variar conforme idade, demanda e objetivos da avaliação.
          </p>
          <div className="mt-8">
            <ArrowButton href={waTopic("a avaliação neuropsicológica")} target="_blank" rel="noopener noreferrer" testId="timeline-cta-button">
              Quero informações sobre avaliação
            </ArrowButton>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

export default Timeline;
